import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Check, Loader2, AlertCircle } from 'lucide-react';
import axios from 'axios';
import Navigation from '@/components/marketing/Navigation';
import Footer from '@/components/marketing/Footer';
import SEO from '@/components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PilotPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organisation: '',
    organisation_type: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      await axios.post(`${API_URL}/api/pilot-request`, formData);
      setIsSubmitted(true);
    } catch (err) {
      setError('Something went wrong. Please try again or email us directly.');
    }
    setIsSubmitting(false);
  };

  const pilotIncludes = [
    '4-6 week structured engagement',
    'Pilot cohort setup (5-25 households)',
    'Permissioning baseline configuration',
    'Templates (Family Governance / Advice Annual Review)',
    'Links & Sources register setup',
    'Weekly check-ins and refinement',
    'Outcome review and rollout planning'
  ];

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-[#FAF7F2]">
        <SEO page="pilot" />
        <Navigation />
        <div className="pt-32 pb-20 px-6 lg:px-8">
          <div className="max-w-xl mx-auto text-center">
            <div className="w-16 h-16 bg-[#A86A3A]/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-8 h-8 text-[#A86A3A]" />
            </div>
            <h1 className="hero-medium text-[#1A1411] mb-4">Thank you for your interest</h1>
            <p className="text-[#6B5B52] mb-8">
              We&apos;ve received your pilot request and will be in touch within 2 business days to discuss next steps.
            </p>
            <Link to="/" className="btn-secondary">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to home
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF7F2]" data-testid="pilot-page">
      <SEO page="pilot" />
      <Navigation />
      
      <div className="pt-32 pb-20 px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <Link to="/" className="inline-flex items-center text-[#5B3627] hover:text-[#3d2419] mb-8">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to home
          </Link>

          <div className="grid lg:grid-cols-2 gap-16">
            {/* Form */}
            <div>
              <h1 className="hero-medium text-[#1A1411] mb-4">Request a pilot</h1>
              <p className="text-[#6B5B52] mb-8">
                Start with a focused pilot to validate outcomes before full rollout.
              </p>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-[#1A1411] mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white border border-[#D8CBBE] focus:border-[#5B3627] focus:outline-none transition-colors"
                    data-testid="pilot-name-input"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-[#1A1411] mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white border border-[#D8CBBE] focus:border-[#5B3627] focus:outline-none transition-colors"
                    data-testid="pilot-email-input"
                  />
                </div>

                <div>
                  <label htmlFor="organisation" className="block text-sm font-medium text-[#1A1411] mb-2">
                    Organisation *
                  </label>
                  <input
                    type="text"
                    id="organisation"
                    name="organisation"
                    required
                    value={formData.organisation}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white border border-[#D8CBBE] focus:border-[#5B3627] focus:outline-none transition-colors"
                    data-testid="pilot-org-input"
                  />
                </div>

                <div>
                  <label htmlFor="organisation_type" className="block text-sm font-medium text-[#1A1411] mb-2">
                    Organisation Type *
                  </label>
                  <select
                    id="organisation_type"
                    name="organisation_type"
                    required
                    value={formData.organisation_type}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white border border-[#D8CBBE] focus:border-[#5B3627] focus:outline-none transition-colors"
                    data-testid="pilot-type-select"
                  >
                    <option value="">Select type...</option>
                    <option value="single-family-office">Single Family Office</option>
                    <option value="multi-family-office">Multi Family Office</option>
                    <option value="advice-firm">Advice Firm</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-[#1A1411] mb-2">
                    Message (optional)
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white border border-[#D8CBBE] focus:border-[#5B3627] focus:outline-none transition-colors resize-none"
                    data-testid="pilot-message-input"
                  />
                </div>

                {error && (
                  <div className="flex items-center gap-2 text-red-600 text-sm">
                    <AlertCircle className="w-4 h-4" />
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary w-full justify-center"
                  data-testid="pilot-submit-btn"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'Submit pilot request'
                  )}
                </button>

                <p className="text-xs text-[#9B8B7E] text-center">
                  We&apos;ll respond within 2 business days. Your information is handled in accordance with our privacy practices.
                </p>
              </form>
            </div>

            {/* Sidebar */}
            <div className="lg:pt-16">
              <div className="bg-[#EFE6DC] p-8">
                <h3 className="heading-3 text-[#1A1411] mb-6">What the pilot includes</h3>
                <ul className="space-y-4">
                  {pilotIncludes.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-[#A86A3A] mt-0.5 flex-shrink-0" />
                      <span className="text-[#6B5B52] text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
